import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Shield, Eye, Lock, Mail, Phone } from "lucide-react";
import Seo from "../components/Seo";

const PolitiqueConfidentialite = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        titre="Politique de confidentialité | Transition Énergie France"
        description="Comment nous collectons et protégeons vos données personnelles, et comment exercer vos droits RGPD."
        chemin="/politique-confidentialite"
      />
      <Header />
      
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <Shield className="h-12 w-12 text-orange-700 mr-3" />
              <h1 className="text-4xl font-bold text-gray-900">Politique de Confidentialité</h1>
            </div>
            <p className="text-xl text-gray-700">
              Transition Énergie France s'engage à protéger vos données personnelles
            </p>
            <p className="text-sm text-gray-700 mt-2">
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
            </p>
          </div>

          <div className="space-y-8">
            <section className="border-l-4 border-orange-500 pl-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Eye className="h-6 w-6 mr-2 text-orange-700" />
                1. Collecte des données
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Nous collectons les données personnelles suivantes lorsque vous utilisez nos services :
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Données d'identité :</strong> nom, prénom, adresse</li>
                  <li><strong>Données de contact :</strong> adresse e-mail, numéro de téléphone</li>
                  <li><strong>Données du projet :</strong> type de travaux, revenus (pour l'éligibilité MaPrimeAdapt')</li>
                  <li><strong>Données de navigation :</strong> cookies, adresse IP, pages visitées</li>
                </ul>
              </div>
            </section>

            <section className="border-l-4 border-blue-500 pl-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. Utilisation des données
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>Vos données personnelles sont utilisées pour :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Traiter votre demande d'accompagnement MaPrimeAdapt'</li>
                  <li>Vous contacter concernant votre projet d'adaptation</li>
                  <li>Calculer votre éligibilité aux aides financières</li>
                  <li>Améliorer nos services et notre site web</li>
                  <li>Vous envoyer des informations sur nos services (avec votre consentement)</li>
                </ul>
              </div>
            </section>

            <section className="border-l-4 border-green-500 pl-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Lock className="h-6 w-6 mr-2 text-green-700" />
                3. Protection et conservation
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  <strong>Sécurité :</strong> Nous mettons en place des mesures techniques et organisationnelles 
                  appropriées pour protéger vos données contre tout accès non autorisé, altération, divulgation 
                  ou destruction.
                </p>
                <p>
                  <strong>Conservation :</strong> Vos données sont conservées pendant la durée nécessaire aux 
                  finalités pour lesquelles elles ont été collectées, conformément aux obligations légales 
                  (maximum 3 ans après la fin de la relation commerciale).
                </p>
              </div>
            </section>

            <section className="border-l-4 border-purple-500 pl-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. Partage des données
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>Vos données peuvent être partagées avec :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>ANAH :</strong> Pour le montage de votre dossier MaPrimeAdapt'</li>
                  <li><strong>Artisans partenaires :</strong> Uniquement avec votre accord explicite</li>
                  <li><strong>Prestataires techniques :</strong> Hébergement, maintenance (sous contrat de confidentialité)</li>
                </ul>
                <p className="font-medium text-orange-700">
                  Nous ne vendons jamais vos données à des tiers.
                </p>
              </div>
            </section>

            <section className="border-l-4 border-red-500 pl-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. Vos droits
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>Conformément au RGPD, vous disposez des droits suivants :</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Droits d'accès et de rectification</h4>
                    <p className="text-sm">Consulter et corriger vos données personnelles</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Droit à l'effacement</h4>
                    <p className="text-sm">Demander la suppression de vos données</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Droit à la portabilité</h4>
                    <p className="text-sm">Récupérer vos données dans un format utilisable</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Droit d'opposition</h4>
                    <p className="text-sm">Vous opposer au traitement de vos données</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="border-l-4 border-yellow-500 pl-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. Cookies
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>Notre site utilise des cookies pour :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Fonctionnement :</strong> Navigation et fonctionnalités essentielles</li>
                  <li><strong>Analyse :</strong> Comprendre l'utilisation du site (avec votre consentement)</li>
                  <li><strong>Amélioration :</strong> Optimiser l'expérience utilisateur</li>
                </ul>
                <p className="text-sm bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                  Les cookies de mesure d'audience ne sont déposés qu'après votre consentement, 
                  recueilli via le bandeau affiché lors de votre première visite. Vous pouvez retirer 
                  votre consentement à tout moment via le lien « Gérer mes cookies » en bas de page, 
                  ou depuis les paramètres de votre navigateur. Votre choix est conservé 6 mois.
                </p>
              </div>
            </section>

            <section className="bg-orange-50 p-6 rounded-lg border border-orange-200">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Mail className="h-6 w-6 mr-2 text-orange-700" />
                Contact et exercice de vos droits
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Pour exercer vos droits ou pour toute question concernant cette politique de confidentialité, 
                  vous pouvez nous contacter :
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-orange-700" />
                    <div>
                      <p className="font-medium">Email</p>
                      <p>contact@transition-energie-france.fr</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-orange-700" />
                    <div>
                      <p className="font-medium">Téléphone</p>
                      <p>09 39 03 54 44</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-4 bg-white rounded-lg border">
                  <p className="font-medium mb-2">Adresse postale :</p>
                  <p>
                    Transition Énergie France<br />
                    Service Protection des Données<br />
                    99 avenue Achille Peretti<br />
                    92200 Neuilly-sur-Seine
                  </p>
                </div>
              </div>
            </section>

            <section className="text-center bg-gray-100 p-6 rounded-lg">
              <p className="text-sm text-gray-700">
                Si vous n'êtes pas satisfait de notre réponse, vous avez le droit de déposer une réclamation 
                auprès de la <strong>CNIL</strong> (Commission Nationale de l'Informatique et des Libertés).
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <a 
                  href="https://www.cnil.fr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-orange-700 hover:text-orange-700 underline"
                >
                  www.cnil.fr
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PolitiqueConfidentialite;